/* VERDETTO — la sola domanda che conta: la strategia batte il mercato, e a che
 * prezzo. In forma piena per il backtest singolo, ridotta a distintivo per le
 * liste e i Preferiti.
 *
 * Anche qui il rendimento non compare da solo: accanto stanno drawdown,
 * operazioni e periodo. Un "+187,4%" senza il resto non è un verdetto. */

import { Distintivo, DistintivoPeriodo, Etichetta } from './Base';
import { ScheletroLista, StatoErrore } from './Stati';
import { calcolaVerdetto } from '../lib/metriche';
import { drawdown as fmtDrawdown, num, pct } from '../lib/formato';
import type { StatoDati } from '../api/usaRichiesta';

export interface PropsVerdetto {
  /** Rendimento della strategia, in percentuale. */
  profitto: number | null;
  /** Rendimento del mercato nello stesso periodo. */
  mercato: number | null;
  drawdown: number | null;
  operazioni: number | null;
  periodo: string;
  stato?: StatoDati;
  onRiprova?: () => void;
}

export function Verdetto({
  profitto,
  mercato,
  drawdown,
  operazioni,
  periodo,
  stato = 'pieno',
  onRiprova,
}: PropsVerdetto) {
  if (stato === 'caricamento') {
    return (
      <div className="sb-verdetto sb-verdetto--neutro">
        <Etichetta>Verdetto · strategia vs mercato</Etichetta>
        <ScheletroLista righe={2} conIcona={false} />
      </div>
    );
  }

  if (stato === 'errore') {
    return (
      <div className="sb-verdetto sb-verdetto--neutro">
        <StatoErrore titolo="Verdetto non calcolabile" onRiprova={onRiprova} />
      </div>
    );
  }

  /* Senza il rendimento del mercato non c'è verdetto da dare. Inventare un
     confronto con zero direbbe "batte il mercato" a qualsiasi guadagno. */
  if (profitto == null || mercato == null) {
    return (
      <div className="sb-verdetto sb-verdetto--neutro">
        <Etichetta>Verdetto · strategia vs mercato</Etichetta>
        <div className="sb-verdetto__valore sb-tono-neutro" style={{ marginTop: 'var(--sb-s-6)' }}>
          n/d
        </div>
        <div className="sb-verdetto__titolo sb-tono-neutro">
          Manca il rendimento del mercato su questo periodo
        </div>
      </div>
    );
  }

  const v = calcolaVerdetto(profitto, mercato);

  return (
    <div className={`sb-verdetto sb-verdetto--${v.tono}`}>
      <Etichetta>Verdetto · strategia vs mercato</Etichetta>

      <div className="sb-terna__testata" style={{ marginTop: 'var(--sb-s-6)' }}>
        <span className={`sb-verdetto__valore sb-tono-${v.tono}`}>{pct(profitto, 1)}</span>
        <span className="sb-verdetto__mercato">vs {pct(mercato, 1)}</span>
      </div>

      <div className={`sb-verdetto__titolo sb-tono-${v.tono}`}>{v.titolo}</div>

      <div className="sb-terna__secondari" style={{ gap: 'var(--sb-s-7)', flexWrap: 'wrap' }}>
        <div>
          <Etichetta>Drawdown</Etichetta>
          <div className="sb-terna__voce-valore sb-tono-perdita" style={{ fontSize: 'var(--sb-t-14)' }}>
            {drawdown == null ? 'n/d' : fmtDrawdown(drawdown)}
          </div>
        </div>
        <div>
          <Etichetta>Operazioni</Etichetta>
          <div className="sb-terna__voce-valore sb-tono-neutro" style={{ fontSize: 'var(--sb-t-14)' }}>
            {operazioni == null ? 'n/d' : num(operazioni)}
          </div>
        </div>
        <div>
          <Etichetta>Periodo</Etichetta>
          <div className="sb-terna__voce-valore sb-tono-neutro" style={{ fontSize: 'var(--sb-t-14)' }}>
            {periodo}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Forma ridotta per liste e Preferiti: BATTE · PARI · SOTTO · PERDE. */
export function DistintivoVerdetto({
  profitto,
  mercato,
}: {
  profitto: number | null;
  mercato: number | null;
}) {
  if (profitto == null || mercato == null) {
    return <Distintivo tono="neutro">n/d</Distintivo>;
  }
  const v = calcolaVerdetto(profitto, mercato);
  return (
    <Distintivo tono={v.tono}>
      {v.etichetta}
    </Distintivo>
  );
}

/** Il periodo accanto al distintivo, per le righe di lista. */
export function VerdettoInLista({
  profitto,
  mercato,
  periodo,
}: {
  profitto: number | null;
  mercato: number | null;
  periodo: string;
}) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sb-s-3)' }}>
      <DistintivoVerdetto profitto={profitto} mercato={mercato} />
      <DistintivoPeriodo>{periodo}</DistintivoPeriodo>
    </span>
  );
}
